// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Divider, Paper, Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../../utils/api';
import { dateTime } from '../../utils/dates';
import { errorMessage } from '../../utils/erreurs';
import { saveFile } from '../../utils/fichiers';
import { CODE } from './commun';

// Écrite dans chaque fichier qui appelle : `backend/tests/test_console_routes.py`
// ne résout une constante de chemin que dans le fichier qui la déclare.
const ORDERS_ROUTE = '/cles-de-pose';

/**
 * Ce que l'atelier fait du fichier d'un ordre, et le graveur qui le lit. La
 * consigne reprend celle du dialogue de préparation, lisible ici sans préparer
 * de clé par qui a déjà le fichier en main.
 *
 * Le graveur publié sur ce serveur est décrit (version, taille, empreinte) avant
 * d'être téléchargé : la taille est annoncée avant l'envoi et l'empreinte
 * permet de vérifier ce qu'on lance. `onToast` reçoit le compte rendu du
 * téléchargement.
 */
export default function KeyWriterPanel({ onToast }) {
  const [keyWriter, setKeyWriter] = useState(null);
  const [downloadingKeyWriter, setDownloadingKeyWriter] = useState(false);

  const loadKeyWriter = useCallback(async () => {
    try {
      const res = await api.get(`${ORDERS_ROUTE}/graveur`);
      setKeyWriter(res.data && typeof res.data === 'object' ? res.data : null);
    } catch (e) {
      // Route absente ou en erreur : le bloc dit « aucun graveur publié »
      // plutôt que de disparaître, ce qui laisserait croire que l'outil
      // n'existe pas.
      setKeyWriter({ disponible: false, raison: errorMessage(e, 'Graveur indisponible') });
    }
  }, []);

  useEffect(() => { loadKeyWriter(); }, [loadKeyWriter]);

  /**
   * Télécharge le binaire depuis l'API, en mémoire, comme l'ordre lui-même.
   *
   * Pas une ancre vers la route : elle n'emporterait pas l'en-tête
   * d'autorisation (403), et la seule parade serait un jeton dans l'URL, donc
   * dans le journal nginx et chez Cloudflare.
   */
  const downloadKeyWriter = async () => {
    setDownloadingKeyWriter(true);
    try {
      const res = await api.get(`${ORDERS_ROUTE}/graveur/telecharger`,
                                { responseType: 'blob' });
      saveFile(new Blob([res.data], { type: 'application/octet-stream' }),
                         keyWriter?.nom || 'VigeosGraveur.exe');
      onToast({ texte: `Graveur ${keyWriter?.version || ''} téléchargé.`, gravite: 'success' });
    } catch (e) {
      onToast({ texte: errorMessage(e, 'Téléchargement du graveur impossible'),
                gravite: 'error' });
    } finally {
      setDownloadingKeyWriter(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Que faire du fichier téléchargé
      </Typography>
      <Typography variant="body2" color="text.secondary" component="div">
        Le <Box component="code" sx={CODE}>ordre-VS-XXXXXX.vigeos.json</Box> n'est
        pas un justificatif : c'est ce que le poste d'atelier lit pour graver la
        clé, et le seul endroit où vit le secret du serveur.
        <ol style={{ margin: '0.5rem 0 0', paddingLeft: '1.2rem' }}>
          <li>Brancher la clé USB à graver (son contenu sera effacé).</li>
          <li>Lancer <strong>VigeosGraveur.exe</strong>, à télécharger ci-dessous.</li>
          <li>Choisir ce fichier, choisir la clé, cliquer Graver.</li>
        </ol>
        <Box sx={{ mt: 1 }}>
          La clé est gravée puis relue, et le fichier est effacé à ce moment-là.
          N'importe quel poste peut graver : l'atelier ne porte aucune identité de
          site, il lit un fichier.
        </Box>
      </Typography>

      <Divider sx={{ my: 2 }} />

      {/* Le graveur est un binaire autonome : un poste neuf grave sans
          cloner le dépôt ni installer Python. */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          startIcon={downloadingKeyWriter ? <CircularProgress size={16} /> : <DownloadIcon />}
          disabled={!keyWriter?.disponible || downloadingKeyWriter}
          onClick={downloadKeyWriter}
        >
          Télécharger le graveur (Windows)
        </Button>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          {keyWriter?.disponible ? (
            <Typography variant="caption" color="text.secondary" component="div">
              version <strong>{keyWriter.version || 'non dite'}</strong>
              {keyWriter.taille ? ` · ${(keyWriter.taille / 1048576).toFixed(1)} Mo` : ''}
              {keyWriter.publie_le ? ` · publié le ${dateTime(keyWriter.publie_le, '—')}` : ''}
              {/* Le fichier s'exécute : son empreinte permet de vérifier que
                  ce qu'on lance est bien ce que Vigeos a publié. */}
              {keyWriter.sha256 && (
                <Box component="div" sx={{ ...CODE, mt: 0.5, display: 'inline-block',
                                           whiteSpace: 'normal', wordBreak: 'break-all' }}>
                  sha256 {keyWriter.sha256}
                </Box>
              )}
              <Box sx={{ mt: 0.5 }}>
                Aucune installation : le fichier contient l'interpréteur et la chaîne
                de fabrication. Windows affichera un avertissement au premier
                lancement (le binaire n'est pas signé) : « Informations
                complémentaires », puis « Exécuter quand même ».
              </Box>
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {keyWriter?.raison || 'Aucun graveur publié sur ce serveur.'}
            </Typography>
          )}
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
        Le graveur va les chercher lui-même : onglet « Ce poste », il télécharge
        l'image du pipeline, le modèle, l'ISO et la clé publique d'administration,
        vérifiées par leur empreinte. Restent à poser à la main la distribution
        Linux et la graine de signature du canal : celle-ci ne se télécharge
        jamais, elle signe les charges de toute la flotte. Le détail est dans
        {' '}<Box component="code" sx={CODE}>edge/site-server-tools/cle-de-pose/graveur/README.md</Box>.
      </Typography>
    </Paper>
  );
}
